import React from 'react';
import classNames from 'classnames';
import { BsPersonFill, BsXLg } from 'react-icons/bs';
import { Dropdown } from '@/components/Dropdown';
import styles from './EmployeeFormRow.module.scss';

// Compact select style for inline employee rows — strips all default chrome
// so the dropdown reads as a plain field, matching the row's text inputs.
const inlineSelectStyles = {
  container: {
    border: 'none',
    borderRadius: '0',
    backgroundColor: 'transparent',
  },
  control: {
    height: '26px',
    minHeight: '26px',
    border: 'none',
    borderRadius: '4px',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
  valueContainer: {
    height: '26px',
    padding: '0',
    backgroundColor: 'transparent',
    borderRadius: '0',
  },
  singleValue: {
    paddingLeft: '0',
    fontSize: '14px',
    color: 'var(--gray-900)',
  },
  input: {
    paddingLeft: '0',
    margin: 0,
    padding: 0,
  },
  placeholder: {
    paddingLeft: '0',
    fontSize: '12px',
    color: 'var(--gray-400)',
  },
};

const inlineSelectErrorStyles = {
  ...inlineSelectStyles,
  control: {
    ...inlineSelectStyles.control,
    boxShadow: 'inset 0 -1.5px 0 var(--error-500)',
  },
};

const EmpInlineDropdown = ({
  label,
  value,
  options,
  placeholder,
  onChange,
  isCreatable,
  onCreateOption,
  error,
}) => (
  <div
    className={classNames(styles.empInlineField, {
      [styles.empInlineFieldError]: !!error,
    })}
  >
    <span className={styles.empInlineFieldLabel}>{label}</span>
    <Dropdown
      value={value ? { label: value, value } : null}
      options={options}
      selectStyles={error ? inlineSelectErrorStyles : inlineSelectStyles}
      placeholder={placeholder}
      isCreatable={isCreatable}
      onChange={(opt) => onChange(opt?.value || '')}
      onCreateOption={onCreateOption}
    />
    {error && <span className={styles.fieldErrorCaption}>{error}</span>}
  </div>
);

/**
 * One manual-entry employee row: avatar, required fields (name, email,
 * role, timezone), and optional fields (desk ID, employee ID).
 *
 * `showDepartment` adds a creatable Department field between Email and
 * Role — used where each employee can belong to a different team (e.g.
 * onboarding, which has no teams yet). Pages where the team is chosen once
 * for the whole batch (e.g. the standalone Add Employee page) omit it.
 *
 * `errors` is `{ fieldName: 'short caption' }` — e.g. `{ role: 'Required' }`
 * highlights the Role field in place instead of a separate error banner.
 */
export const EmployeeFormRow = ({
  row,
  index,
  timezoneOptions,
  roleOptions,
  onChange,
  onRemove,
  canRemove,
  showDepartment = false,
  departmentOptions = [],
  onAddDepartment,
  errors = {},
}) => {
  const initial = row.name ? row.name.charAt(0).toUpperCase() : null;

  return (
    <div className={styles.empRow}>
      <div
        className={classNames(styles.empAvatar, {
          [styles.empAvatarFilled]: !!initial,
        })}
      >
        {initial || <BsPersonFill size={14} color="var(--gray-400)" />}
      </div>

      <div className={styles.empRowContent}>
        <div className={styles.empFields}>
          <div
            className={classNames(styles.empInlineField, {
              [styles.empInlineFieldError]: !!errors.name,
            })}
          >
            <span className={styles.empInlineFieldLabel}>Full Name *</span>
            <input
              className={classNames(styles.empInlineFieldInput, {
                [styles.empInlineFieldInputError]: !!errors.name,
              })}
              value={row.name}
              onChange={(e) => onChange(index, 'name', e.target.value)}
              placeholder="Enter name"
            />
            {errors.name && (
              <span className={styles.fieldErrorCaption}>{errors.name}</span>
            )}
          </div>

          <div
            className={classNames(
              styles.empInlineField,
              styles.empInlineFieldWide,
              { [styles.empInlineFieldError]: !!errors.email }
            )}
          >
            <span className={styles.empInlineFieldLabel}>Email Address *</span>
            <input
              className={classNames(styles.empInlineFieldInput, {
                [styles.empInlineFieldInputError]: !!errors.email,
              })}
              value={row.email}
              onChange={(e) => onChange(index, 'email', e.target.value)}
              placeholder="email@example.com"
              type="email"
            />
            {errors.email && (
              <span className={styles.fieldErrorCaption}>{errors.email}</span>
            )}
          </div>

          {showDepartment && (
            <EmpInlineDropdown
              label="Department *"
              value={row.team}
              options={departmentOptions}
              placeholder="Add / Select"
              isCreatable
              onChange={(val) => onChange(index, 'team', val)}
              onCreateOption={(val) => {
                onChange(index, 'team', val);
                onAddDepartment?.(val);
              }}
              error={errors.team}
            />
          )}

          <EmpInlineDropdown
            label="Role *"
            value={row.role}
            options={roleOptions}
            placeholder="Select"
            onChange={(val) => onChange(index, 'role', val)}
            error={errors.role}
          />

          <div
            className={classNames(
              styles.empInlineField,
              styles.empInlineFieldWide,
              { [styles.empInlineFieldError]: !!errors.timeZone }
            )}
          >
            <span className={styles.empInlineFieldLabel}>Timezone *</span>
            <Dropdown
              value={
                row.timeZone
                  ? { label: row.timeZone, value: row.timeZone }
                  : null
              }
              options={timezoneOptions}
              selectStyles={
                errors.timeZone ? inlineSelectErrorStyles : inlineSelectStyles
              }
              placeholder="Select timezone"
              onChange={(opt) => onChange(index, 'timeZone', opt?.value || '')}
            />
            {errors.timeZone && (
              <span className={styles.fieldErrorCaption}>
                {errors.timeZone}
              </span>
            )}
          </div>
        </div>

        <div className={styles.empOptionalFields}>
          <div
            className={classNames(styles.empOptionalField, {
              [styles.empOptionalFieldError]: !!errors.deskId,
            })}
          >
            <span className={styles.empOptionalFieldLabel}>Desk ID:</span>
            <input
              className={classNames(styles.empOptionalFieldInput, {
                [styles.empInlineFieldInputError]: !!errors.deskId,
              })}
              value={row.deskId}
              onChange={(e) => onChange(index, 'deskId', e.target.value)}
              placeholder="Optional"
            />
            {errors.deskId && (
              <span className={styles.fieldErrorCaption}>{errors.deskId}</span>
            )}
          </div>
          <div className={styles.empOptionalField}>
            <span className={styles.empOptionalFieldLabel}>Employee ID:</span>
            <input
              className={styles.empOptionalFieldInput}
              value={row.employeeId}
              onChange={(e) => onChange(index, 'employeeId', e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
      </div>

      {canRemove && (
        <button
          type="button"
          className={styles.empRemoveBtn}
          onClick={() => onRemove(index)}
          aria-label="Remove employee"
        >
          <BsXLg size={11} />
        </button>
      )}
    </div>
  );
};
